/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderFormSchemaFactory,
  FormRootSchema,
  FormStateSchema,
  whenUsOrAuRegion,
  whenUsRegion,
} from '@gh/builder-shared';
import { BuilderPermission, SalesFolderStatus } from '@gh/builder-shared-data';
import {
  DateTimeTypes,
  FormState,
  isRouteForCreationMode,
  MaskedTypes,
  NumberTypes,
  UserContext,
  whenCreateOrEditMode,
} from '@gh/common-shared';
import { FormMode, getControlValue, markControlAsDirty, setControlValue } from '@gh/core-ui';
import { JobDppDetailsGeneral } from '../../../resources/job.model';
import { ContactListItem } from '../../../resources/job-details-tab';

@Injectable()
export class JobDppGeneralTabFormState extends FormState<JobDppDetailsGeneral> {
  constructor(schemaFactory: BuilderFormSchemaFactory,
              private route: ActivatedRoute,
              private userContext: UserContext) {
    super(schemaFactory);
  }

  get salesFolderStatus(): SalesFolderStatus {
    return getControlValue(this.group, 'salesFolderStatus');
  }

  setJobAdminActive(isActive: boolean): void {
    setControlValue(this.group, 'isJobAdminActive', isActive);
  }

  setCounty(county: ContactListItem): void {
    setControlValue(this.group, 'county', county);
    markControlAsDirty(this.group, 'county');
  }

  getCounty(): ContactListItem {
    return getControlValue(this.group, 'county');
  }

  protected buildSchema(_: JobDppDetailsGeneral, { root }: FormStateSchema): FormRootSchema {
    const mode = isRouteForCreationMode(this.route)
      ? FormMode.CreateMode : (this.userContext.hasPermission(BuilderPermission.JobAdmin.JobDetails.EDIT)
        ? FormMode.EditMode : FormMode.ViewMode);
    return root({
      mode,
      enabled: whenCreateOrEditMode(),
      children: {
        salesFolderName: { enabled: false },
        salesFolderId: void 0,
        salesFolderStatus: { enabled: false },
        salesFolderNotProceedingReason: { enabled: false },
        salesFolderAddedDate: { enabled: false },
        salesPersonName: { enabled: false },
        jobAdmin: void 0,
        isJobAdminActive: void 0,
        contractAmount: { type: NumberTypes.Price },
        saleDate: { enabled: false, type: DateTimeTypes.ShortDate },
        notProceedingDate: { enabled: false, type: DateTimeTypes.ShortDate },
        memo: { enabled: whenCreateOrEditMode() },
        county: { enabled: mode === FormMode.CreateMode || mode === FormMode.EditMode },
        countyId: void 0,
        countyName: void 0,
        isNotProceeding: { enabled: false },
        siteName: void 0,
        siteLotNumber: void 0,
        siteReference: { visible: whenUsRegion() },
        siteStreet: void 0,
        siteStreetNumber: void 0,
        siteSuburb: void 0,
        sitePostcode: { visible: whenUsOrAuRegion(), type: MaskedTypes.Zip },
      },
    });
  }
}
